import { createFilterBuilder } from '@jebel/utils';

import {
  useInactivateSchoolMembersMutation,
  UserSchoolMembershipFilter,
  UserKeyFilter,
} from 'shared/graphql';
import { useSchoolConfiguration, useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

const INACTIVATING_MEMBER_MESSAGE_KEY = 'INACTIVATING_MEMBER_MESSAGE';

type Options = Parameters<typeof useInactivateSchoolMembersMutation>[0];

/**
 * Custom hook to inactivate a school membership.
 *
 * This hook provides a function to inactivate a school membership for a school and handles
 * success and error notifications.
 *
 * @returns An object containing:
 * - `mutate`: A function to inactivate a school membership.
 * - `loading`: A boolean indicating if the mutation is in progress.
 */
export function useInactivateMember(options?: Options) {
  const { showMessage, showSuccess, showError, dismiss } = useToast();
  const { data: school } = useSchoolConfiguration();

  const [mutation, { loading }] = useInactivateSchoolMembersMutation(options);

  const mutate = async (user: UserKeyFilter) => {
    if (!school) {
      return;
    }

    try {
      showMessage(`Inactivating school membership "${user.email ?? user.id}".`, {
        id: INACTIVATING_MEMBER_MESSAGE_KEY,
      });

      const filter = createFilterBuilder<UserSchoolMembershipFilter>();

      if (user.id) {
        filter.or({ user: { id: { equals: user.id } } });
      }

      if (user.email) {
        filter.or({ user: { email: { equals: user.email } } });
      }

      await mutation({
        variables: {
          filter: filter.build(),
          school: { id: school.id },
        },
      });

      showSuccess(`Member "${user.email ?? user.id}" has been inactivated`);
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        showError(err.message, { reportable: false });
      }
    } finally {
      dismiss(INACTIVATING_MEMBER_MESSAGE_KEY);
    }
  };

  return {
    mutate,
    loading,
  };
}
